import React, { useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import AuditLogTooltip from './AuditLogTooltip';
import { AuditEventSource, AuditEventSummaryResponse } from 'api/adgang';

// Kategorisk palett bygd på Miljødirektoratets merkevarefarger (teal/grønn) med et par varme
// aksenter (pumpkin/ocher) for å skille slices. Suksess bruker merkevarens teal, feil bruker
// applikasjonens feilmeldingsrød.
const CATEGORY_COLORS = ['#009B96', '#004B46', '#00615C', '#56BAB7', '#EF7D00', '#9BD8D6', '#86660E', '#99C0BE'];
const SUCCESS_COLOR = '#009B96';
const FAILURE_COLOR = '#CA0000';

const num = (v: unknown): number => Number(v ?? 0);

// Relativ endring (%) mellom første og andre halvdel av perioden - en enkel trendindikator
// utledet fra perDay, uten behov for et eget "forrige periode"-kall.
function relTrend(series: number[]): { pct: number; up: boolean } | null {
  if (series.length < 2) return null;
  const mid = Math.floor(series.length / 2);
  // Snitt per dag, slik at halvdeler med ulik lengde (oddetall dager) ikke gir skjev trend.
  const avg = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;
  const first = avg(series.slice(0, mid));
  const second = avg(series.slice(mid));
  // Ingen meningsfull prosent fra et nullgrunnlag.
  if (first === 0) return null;
  const pct = ((second - first) / first) * 100;
  return { pct, up: pct >= 0 };
}

// Endring i prosentpoeng (pp) for rate-baserte tall (feilrate).
function ppTrend(rates: number[]): { pp: number; up: boolean } | null {
  if (rates.length < 2) return null;
  const mid = Math.floor(rates.length / 2);
  const avg = (a: number[]) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0);
  const pp = avg(rates.slice(mid)) - avg(rates.slice(0, mid));
  return { pp, up: pp >= 0 };
}

function sourceLabel(source: AuditEventSource | string | undefined): string {
  if (source === AuditEventSource.DirectoryAudit) return 'Katalogendring';
  if (source === AuditEventSource.SignIn) return 'Innlogging';
  return source ?? 'Ukjent';
}

function AuditLogSummary({ summary }: { summary?: AuditEventSummaryResponse }) {
  const total = num(summary?.totalEvents);
  const failed = num(summary?.failedEvents);
  const success = Math.max(total - failed, 0);
  const failureRate = total > 0 ? Math.round((failed / total) * 1000) / 10 : 0;

  const perDay = useMemo(
    () =>
      (summary?.perDay ?? []).map((d) => {
        const count = num(d.count);
        const failedCount = num(d.failedCount);
        return {
          label: d.date
            ? new Date(d.date).toLocaleDateString('no-NO', { day: '2-digit', month: '2-digit' })
            : '',
          Vellykket: Math.max(count - failedCount, 0),
          Feilet: failedCount
        };
      }),
    [summary]
  );

  const topActivities = useMemo(
    () =>
      (summary?.topActivities ?? [])
        .slice(0, 10)
        .map((a) => ({ activity: a.activity ?? 'Ukjent', count: num(a.count) })),
    [summary]
  );

  const perSource = useMemo(
    () => (summary?.perSource ?? []).map((s) => ({ name: sourceLabel(s.source), value: num(s.count) })),
    [summary]
  );

  const resultData = [
    { name: 'Vellykket', value: success },
    { name: 'Feilet', value: failed }
  ];

  // Daglige serier per kort, brukt til sparklines og trend.
  const series = useMemo(() => {
    const days = summary?.perDay ?? [];
    const counts = days.map((d) => num(d.count));
    const faileds = days.map((d) => num(d.failedCount));
    const successes = counts.map((c, i) => Math.max(c - faileds[i], 0));
    const rates = counts.map((c, i) => (c > 0 ? (faileds[i] / c) * 100 : 0));
    return { counts, faileds, successes, rates };
  }, [summary]);

  const trendFor = (t: { pct: number; up: boolean } | null, higherIsBad: boolean) =>
    t ? { text: `${t.up ? '▲' : '▼'} ${Math.abs(t.pct).toFixed(1)} %`, good: t.up ? !higherIsBad : higherIsBad } : null;

  const ppFor = (t: { pp: number; up: boolean } | null, higherIsBad: boolean) =>
    t ? { text: `${t.up ? '▲' : '▼'} ${Math.abs(t.pp).toFixed(1)} pp`, good: t.up ? !higherIsBad : higherIsBad } : null;

  return (
    <>
      <Row className='pb-3'>
        <Col sm={6} lg={3} className='mb-2'>
          <StatCard
            label='Hendelser totalt'
            value={total.toLocaleString('no-NO')}
            series={series.counts}
            color={SUCCESS_COLOR}
            trend={trendFor(relTrend(series.counts), false)}
          />
        </Col>
        <Col sm={6} lg={3} className='mb-2'>
          <StatCard
            label='Vellykkede'
            value={success.toLocaleString('no-NO')}
            series={series.successes}
            color={SUCCESS_COLOR}
            trend={trendFor(relTrend(series.successes), false)}
          />
        </Col>
        <Col sm={6} lg={3} className='mb-2'>
          <StatCard
            label='Feilede'
            value={failed.toLocaleString('no-NO')}
            series={series.faileds}
            color={FAILURE_COLOR}
            trend={trendFor(relTrend(series.faileds), true)}
          />
        </Col>
        <Col sm={6} lg={3} className='mb-2'>
          <StatCard
            label='Feilrate'
            value={`${failureRate} %`}
            series={series.rates}
            color={FAILURE_COLOR}
            trend={ppFor(ppTrend(series.rates), true)}
          />
        </Col>
      </Row>

      <Row className='pb-3'>
        <Col sm={12} lg={8} className='mb-3'>
          <ChartCard title='Hendelser over tid'>
            {perDay.length > 0 ? (
              <ResponsiveContainer width='100%' height={260}>
                <AreaChart data={perDay} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray='3 3' vertical={false} />
                  <XAxis dataKey='label' tick={{ fontSize: 12 }} minTickGap={16} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={40} />
                  <Tooltip content={<AuditLogTooltip />} />
                  <Legend />
                  <Area
                    type='linear'
                    dataKey='Vellykket'
                    stackId='1'
                    stroke={SUCCESS_COLOR}
                    fill={SUCCESS_COLOR}
                    fillOpacity={0.5}
                    dot={{ r: 3, strokeWidth: 0, fill: SUCCESS_COLOR }}
                    activeDot={{ r: 5 }}
                  />
                  <Area
                    type='linear'
                    dataKey='Feilet'
                    stackId='1'
                    stroke={FAILURE_COLOR}
                    fill={FAILURE_COLOR}
                    fillOpacity={0.6}
                    dot={{ r: 3, strokeWidth: 0, fill: FAILURE_COLOR }}
                    activeDot={{ r: 5 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart />
            )}
          </ChartCard>
        </Col>
        <Col sm={12} lg={4} className='mb-3'>
          <ChartCard title='Hendelser per resultat'>
            {total > 0 ? (
              <ResponsiveContainer width='100%' height={260}>
                <PieChart>
                  <Pie
                    data={resultData}
                    dataKey='value'
                    nameKey='name'
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    <Cell fill={SUCCESS_COLOR} />
                    <Cell fill={FAILURE_COLOR} />
                    <Label
                      value={total.toLocaleString('no-NO')}
                      position='center'
                      style={{ fontSize: 20, fontWeight: 700 }}
                    />
                  </Pie>
                  <Tooltip content={<AuditLogTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart />
            )}
          </ChartCard>
        </Col>
      </Row>

      <Row className='pb-3'>
        <Col sm={12} lg={6} className='mb-3'>
          <ChartCard title='Mest aktive hendelser'>
            {topActivities.length > 0 ? (
              <ResponsiveContainer width='100%' height={300}>
                <BarChart
                  data={topActivities}
                  layout='vertical'
                  margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
                >
                  <CartesianGrid strokeDasharray='3 3' horizontal={false} />
                  <XAxis type='number' allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis
                    type='category'
                    dataKey='activity'
                    width={140}
                    tick={{ fontSize: 11 }}
                    interval={0}
                  />
                  <Tooltip content={<AuditLogTooltip />} />
                  <Bar dataKey='count' name='Antall' fill={CATEGORY_COLORS[0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart />
            )}
          </ChartCard>
        </Col>
        <Col sm={12} lg={6} className='mb-3'>
          <ChartCard title='Hendelser per kilde'>
            {perSource.length > 0 ? (
              <ResponsiveContainer width='100%' height={300}>
                <PieChart>
                  <Pie
                    data={perSource}
                    dataKey='value'
                    nameKey='name'
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {perSource.map((entry, i) => (
                      <Cell key={entry.name} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<AuditLogTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart />
            )}
          </ChartCard>
        </Col>
      </Row>
    </>
  );
}

function StatCard({
  label,
  value,
  series,
  color,
  trend
}: {
  label: string;
  value: React.ReactNode;
  series?: number[];
  color?: string;
  trend?: { text: string; good: boolean } | null;
}) {
  const accent = color ?? SUCCESS_COLOR;
  const chartData = (series ?? []).map((v, i) => ({ i, v }));

  return (
    <div className='border rounded p-3 h-100'>
      <div className='text-muted'>{label}</div>
      <div className='fs-3 fw-bold' style={{ color: accent }}>
        {value}
      </div>
      {series && series.length > 1 && (
        <ResponsiveContainer width='100%' height={36}>
          <AreaChart data={chartData} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
            <XAxis hide dataKey='i' />
            <YAxis hide domain={['dataMin', 'dataMax']} />
            <Area
              type='linear'
              dataKey='v'
              stroke={accent}
              fill={accent}
              fillOpacity={0.2}
              strokeWidth={1.5}
              dot={{ r: 1.5, strokeWidth: 0, fill: accent }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
      {trend && (
        <div className='audit-trend' style={{ fontSize: 12, color: trend.good ? SUCCESS_COLOR : FAILURE_COLOR }}>
          {trend.text} <span className='text-muted'>vs forrige periode</span>
        </div>
      )}
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className='border rounded p-3 h-100'>
      <div className='fw-bold mb-2'>{title}</div>
      {children}
    </div>
  );
}

function EmptyChart() {
  return (
    <div className='text-muted d-flex align-items-center justify-content-center' style={{ height: 260 }}>
      Ingen data
    </div>
  );
}

export default AuditLogSummary;
